import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { apiCall } from '../config/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'trainer' | 'student' | 'guest' | 'external';
  student_category?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; showChangelog?: boolean }>;
  register: (name: string, email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  pendingStudentsCount: number;
  updatePendingStudentsCount: (count: number) => void;
  pendingPaymentsCount: number;
  updatePendingPaymentsCount: (count: number) => void;
  markChangelogAsSeen: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingStudentsCount, setPendingStudentsCount] = useState(0);
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const data = await apiCall('/auth/me', { method: 'GET' });
        if (data.user) {
          setUser(data.user);
          // Se for trainer, carregar contador de alunos pendentes
          if (data.user.role === 'trainer') {
            loadPendingStudentsCount();
            loadPendingPaymentsCount();
          }
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loadPendingStudentsCount = async () => {
    try {
      const data = await apiCall('/auth/pending-students', { method: 'GET' });
      if (data.pending_students) {
        setPendingStudentsCount(data.pending_students.length);
      }
    } catch (error) {
      console.error('Erro ao carregar contador de alunos pendentes:', error);
    }
  };

  const updatePendingStudentsCount = (count: number) => {
    setPendingStudentsCount(count);
  };

  const loadPendingPaymentsCount = async () => {
    try {
      const data = await apiCall('/payments/pending', { method: 'GET' });
      if (data.pending_payments) {
        setPendingPaymentsCount(data.pending_payments.length);
      }
    } catch (error) {
      console.error('Erro ao carregar contador de pagamentos pendentes:', error);
    }
  };

  const updatePendingPaymentsCount = (count: number) => {
    setPendingPaymentsCount(count);
  };

  const login = async (email: string, password: string) => {
    try {
      const data = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });

      if (data.token) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        
        // Se for trainer, carregar contador de alunos pendentes
        if (data.user.role === 'trainer') {
          loadPendingStudentsCount();
          loadPendingPaymentsCount();
        }
        
        return { 
          success: true, 
          showChangelog: data.showChangelog || false 
        };
      } else {
        return { success: false, message: data.error || 'Erro no login' };
      }
    } catch (error: any) {
      return { success: false, message: error.message || 'Erro ao comunicar com o servidor' };
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const data = await apiCall('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ name, email, password })
      });

      if (data.message) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Erro no registo' };
      }
    } catch (error: any) {
      return { success: false, message: error.message || 'Erro ao comunicar com o servidor' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setPendingStudentsCount(0);
    setPendingPaymentsCount(0);
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      const response = await apiCall('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword })
      });

      return response.success || false;
    } catch (error) {
      return false;
    }
  };

  const markChangelogAsSeen = async (): Promise<void> => {
    try {
      await apiCall('/auth/mark-changelog-seen', {
        method: 'POST'
      });
    } catch (error) {
      console.error('Erro ao marcar changelog como visto:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    changePassword,
    pendingStudentsCount,
    updatePendingStudentsCount,
    pendingPaymentsCount,
    updatePendingPaymentsCount,
    markChangelogAsSeen
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 